// src/components/admin/AdminStats.tsx
import React, { useEffect, useState } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { useAdminStatStore } from '../../store/adminStore';
import AdminProductCard from './AdminProductCard';
import Loader from '../loader/Loader';
import Pagination from '../pagination/Pagination';
import { Product } from '../../types';

interface AdminStatsProps {
    setActiveTab: (tab: string) => void;
    onFix: (product: Product) => void;
}

const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
};

const AdminStats: React.FC<AdminStatsProps> = ({ setActiveTab, onFix }) => {
    const { adminStat, fetchStats } = useAdminStatStore();
    const [currentPage, setCurrentPage] = useState<number>(1);
    const itemsPerPage = 12;

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const formattedAdditions = (adminStat?.additionsByDay || []).map((item) => ({
        ...item,
        date: formatDate(item.date),
    }));

    const productList = adminStat?.productList || [];
    const totalPages = Math.ceil(productList.length / itemsPerPage);
    const paginatedProducts = productList.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <div className="p-4">
            <h2 className="text-2xl font-bold mb-4">Статистика администратора</h2>
            <div className="mb-4">
                <p>
                    <strong>Всего добавлено товаров:</strong> {adminStat?.totalProducts}
                </p>
            </div>
            <h3 className="text-xl font-semibold mb-2">Добавления товаров по дням</h3>
            <div style={{ height: 400 }}>
                <ResponsiveBar
                    data={formattedAdditions}
                    keys={['count']}
                    indexBy="date"
                    margin={{ top: 50, right: 50, bottom: 50, left: 60 }}
                    padding={0.3}
                    colors={{ scheme: 'accent' }}
                    axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Дата',
                        legendPosition: 'middle',
                        legendOffset: 32,
                    }}
                    axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Количество',
                        legendPosition: 'middle',
                        legendOffset: -40,
                    }}
                />
            </div>
            <h3 className="text-xl font-semibold mt-6 mb-2">Список ваших товаров</h3>
            <div className="w-full">
                {!adminStat && <Loader />}
                <div className="grid sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                    {paginatedProducts.map((product) => (
                        <AdminProductCard
                            key={product.id}
                            product={product}
                            onFix={(prod) => {
                                onFix(prod);
                                setActiveTab("updateProduct");
                            }}
                        />
                    ))}
                </div>
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page: number) => setCurrentPage(page)}
                />
            </div>
        </div>
    );
};

export default AdminStats;
